import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  CourseEnrollmentStatus,
  GetViewerProgramEnrollmentsWithCoverageDocument,
  GetViewerProgramEnrollmentsWithCoverageQuery,
  ProgramEnrollmentStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { EnrolledProgramSummary } from './programs';

/** One row of the query, before this module filters it down to GSA. */
type ProgramEnrollmentQueryRow =
  GetViewerProgramEnrollmentsWithCoverageQuery['viewer']['programEnrollments'][number];

export interface GsaEnrollment {
  id: string;
  programId: string;
  /**
   * What `/program/:programId` actually wants — see AppRoutesPaths.Program for
   * why that route param carries an abbreviation rather than an id.
   */
  programAbbreviation: string;
  /**
   * Whether a live subscription unlocks THIS program. Per enrollment, not per
   * learner: one plan covers a set of programs, so a subscriber can hold two
   * enrollments and have paid modules open in one and shut in the other.
   */
  covered: boolean;
  program: EnrolledProgramSummary;
  /**
   * `CourseDefinition` ids the learner has graduated in this program —
   * `CourseEnrollment.status` is the cancellation-safe rollup, so this is a
   * straight enum comparison with no need to descend into class attempts.
   */
  completedCourseIds: string[];
}

export interface CompletedProgram {
  id: string;
  title: string;
  /** Category tag shown as a saffron badge. Static: GSA has no program-category concept. */
  category: string;
  description: string;
  /** What `/program/:programId` actually wants — see `programAbbreviation` above. */
  abbreviation: string;
}

interface UseGsaEnrollmentsResult {
  /** The programs the learner is working through. */
  current: GsaEnrollment[];
  /** The programs the learner has graduated from. */
  past: CompletedProgram[];
  /**
   * Whether the learner has ever had a GSA program enrollment, in any status -
   * ongoing, graduated, failed, cancelled, deferred, on leave, or still
   * pending. Once true, they've demonstrated they're a GSA learner and stay
   * one: this is deliberately broader than `current`/`past`, which are each
   * scoped to a single status for their own (unrelated) display purposes.
   */
  hasEverBeenGsaLearner: boolean;
  loading: boolean;
}

/**
 * Turns one enrollment into something renderable. `courses` has no source: the
 * backend has no course schema for GSA programs, so the card renders its
 * name, description and progress and nothing else until one exists. Which
 * courses are complete does have a source, though — `courseEnrollments`, kept
 * separate from `program` since it is per-learner rather than program copy.
 */
const toGsaEnrollment = (row: ProgramEnrollmentQueryRow): GsaEnrollment => ({
  id: row.id,
  programId: row.program.id,
  programAbbreviation: row.program.abbreviation,
  covered: row.program.stripeCoverage.covered,
  program: {
    title: row.program.name,
    description: row.program.description ?? '',
  },
  completedCourseIds: row.courseEnrollments
    .filter((ce) => ce.status === CourseEnrollmentStatus.Graduated)
    .map((ce) => ce.course.id),
});

const toCompletedProgram = (
  row: ProgramEnrollmentQueryRow
): CompletedProgram => ({
  id: row.id,
  title: row.program.name,
  category: 'Skills Academy',
  description: row.program.description ?? '',
  abbreviation: row.program.abbreviation,
});

/**
 * The GSA programs the learner is working through (`current`) and has
 * graduated from (`past`).
 *
 * `isUniversityProgram` is the only discriminator the backend has, so the
 * degree enrollments are filtered out here rather than asked for separately —
 * one learner can hold both, and the GSA surfaces must not show a degree.
 *
 * `current` is ONGOING only: GRADUATED and FAILED are over, DEFERRED and
 * LEAVE_OF_ABSENCE are paused by the registrar, and PENDING has not started —
 * none of them is a program to continue. `past` is GRADUATED only - FAILED is
 * over but was never completed, so it is not a "past program" either.
 *
 * The query behind it returns every enrollment, both product types and every
 * status — `programEnrollments` takes no arguments — so both filters below are
 * this module's work, not the server's. It is deliberately its own document
 * rather than GetViewerLearningDetails, which drags in GPA, planned courses,
 * class enrollments and badges for the degree screens. In set-edu that query
 * is only used by DegreeHome, so it would never be warm in the cache when the
 * GSA home needs it.
 */
export const useGsaEnrollments = (): UseGsaEnrollmentsResult => {
  const { data, loading } = useQuery(
    GetViewerProgramEnrollmentsWithCoverageDocument,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const rows = useMemo(() => data?.viewer?.programEnrollments ?? [], [data]);

  const current = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            !row.program.isUniversityProgram &&
            row.status === ProgramEnrollmentStatus.Ongoing
        )
        .map(toGsaEnrollment),
    [rows]
  );

  // GRADUATED only, matching item 3's ask - FAILED is over too, but it is not
  // a "past program" a learner completed, so it stays off this list.
  const past = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            !row.program.isUniversityProgram &&
            row.status === ProgramEnrollmentStatus.Graduated
        )
        .map(toCompletedProgram),
    [rows]
  );

  // No status filter at all, unlike `current`/`past` above: a cancelled or
  // failed GSA enrollment still means this person has been a GSA learner.
  const hasEverBeenGsaLearner = useMemo(
    () => rows.some((row) => !row.program.isUniversityProgram),
    [rows]
  );

  return { current, past, hasEverBeenGsaLearner, loading: loading && !data };
};
